import { createTheme } from "@mui/material/styles";
import { isMobile } from "react-device-detect";
import { Slide, SlideProps } from "@mui/material";
import DefaultPersonalizationConfig from "../PersonalizationComponents/DefaultPersonalizationConfig";
import BrandingType from "../PersonalizationComponents/BrandingType";

// https://mui.com/material-ui/customization/theming/#themeprovider
// https://mui.com/material-ui/customization/palette/
// https://zenoo.github.io/mui-theme-creator/

const isMobileDevice = isMobile;

const salus_theme = createTheme({
  palette: {
    primary: {
      main: "#166bea",
    },
    secondary: {
      main: "#16d5ea",
    },
    background: {
      //@ts-expect-error
      dark: "#e1e1e1",
      default: "#f0f0f0",
      paper: "#ffffff",
    },
    error: {
      main: "#f34254",
    },
    success: {
      main: "#00e327",
    },
    warning: {
      main: "#ea166b",
    },
    info: {
      main: "#2b16ea",
    },
  },
  shape: {
    borderRadius: 8,
  },
  spacing: 8,
  typography: {
    htmlFontSize: isMobileDevice ? 14 : 16,
    fontSize: isMobileDevice ? 12 : 14,
    h1: {
      fontSize: isMobileDevice ? "4.8rem" : "6rem",
    },
    h2: {
      fontSize: isMobileDevice ? "3rem" : "3.75rem",
    },
    h3: {
      fontSize: isMobileDevice ? "2.4rem" : "3rem",
    },
    h4: {
      fontSize: isMobileDevice ? "1.7rem" : "2.125rem",
    },
    h5: {
      fontSize: isMobileDevice ? "1.5rem" : "1.875rem",
    },
    h6: { fontWeight: 400, fontSize: isMobileDevice ? "1.2rem" : "1.5rem" },

    body1: {
      fontSize: isMobileDevice ? "0.84rem" : "1.05rem",
    },
    body2: {
      fontSize: isMobileDevice ? "0.76rem" : "0.95rem",
    },
    subtitle1: {
      fontSize: isMobileDevice ? "0.8rem" : "1rem",
    },
    subtitle2: {
      fontWeight: 400,
      fontSize: isMobileDevice ? "0.7rem" : "0.875rem",
    },
    button: {
      fontSize: isMobileDevice ? "0.7rem" : "0.875rem",
    },
    caption: {
      fontSize: isMobileDevice ? "0.68rem" : "0.85rem",
    },
  },
  components: {
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          fontSize: "1rem",
        },
      },
    },
    MuiDialogTitle: {
      styleOverrides: {
        root: {
          color: '#2b16ea',
          backgroundColor: isMobileDevice ? "inherit" : "inherit",
        }
      }
    },
    MuiFormHelperText: {
      styleOverrides: {
        root: {
          fontSize: "0.85rem",
        },
      },
    },
    MuiDialog: {
      defaultProps: {
        TransitionComponent: isMobileDevice ? Slide : undefined,
        fullScreen: isMobileDevice,
        TransitionProps: isMobileDevice ? ({ direction: "up" } as SlideProps) : undefined,
      },
    },
    MuiDialogActions: {
      styleOverrides: {
        root: {
          backgroundColor: isMobileDevice ? "#e1e1e1" : "inherit",
        },
      },
    },
  },
});

const jpm_theme = createTheme({
  palette: {
    primary: {
      main: "#006f94",
    },
    secondary: {
      main: "#8f6739",
    },
    background: {
      //@ts-expect-error
      dark: "#f7f1ef",
      default: "#faf8fc",
      paper: "#ffffff",
    },
    error: {
      main: "#d843a1",
    },
    success: {
      main: "#187937",
    },
    warning: {
      main: "#ea166b",
      //@ts-expect-error
      text: "#fd7e14",
    },
    info: {
      main: "#4651c4",
    },
  },
  shape: {
    borderRadius: 8,
  },
  spacing: 8,
  typography: {
    htmlFontSize: isMobileDevice ? 14 : 16,
    fontSize: isMobileDevice ? 12 : 14,
    h1: {
      fontSize: isMobileDevice ? "4.8rem" : "6rem",
    },
    h2: {
      fontSize: isMobileDevice ? "3rem" : "3.75rem",
    },
    h3: {
      fontSize: isMobileDevice ? "2.4rem" : "3rem",
    },
    h4: {
      fontSize: isMobileDevice ? "1.7rem" : "2.125rem",
    },
    h5: {
      fontSize: isMobileDevice ? "1.5rem" : "1.875rem",
    },
    h6: { fontWeight: 400, fontSize: isMobileDevice ? "1.2rem" : "1.5rem" },

    body1: {
      fontSize: isMobileDevice ? "0.84rem" : "1.05rem",
    },
    body2: {
      fontSize: isMobileDevice ? "0.76rem" : "0.95rem",
    },
    subtitle1: {
      fontSize: isMobileDevice ? "0.8rem" : "1rem",
    },
    subtitle2: {
      fontWeight: 400,
      fontSize: isMobileDevice ? "0.7rem" : "0.875rem",
    },
    button: {
      fontSize: isMobileDevice ? "0.7rem" : "0.875rem",
    },
    caption: {
      fontSize: isMobileDevice ? "0.68rem" : "0.85rem",
    },
  },
  components: {
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          fontSize: "1rem",
        },
      },
    },
    MuiDialogTitle: {
      styleOverrides: {
        root: {
          color: "#4651c4",
          backgroundColor: isMobileDevice ? "inherit" : "inherit",
        },
      },
    },
    MuiFormHelperText: {
      styleOverrides: {
        root: {
          fontSize: "0.85rem",
        },
      },
    },
    MuiDialog: {
      defaultProps: {
        TransitionComponent: isMobileDevice ? Slide : undefined,
        fullScreen: isMobileDevice,
        TransitionProps: isMobileDevice ? ({ direction: "up" } as SlideProps) : undefined,
      },
    },
    MuiDialogActions: {
      styleOverrides: {
        root: {
          backgroundColor: isMobileDevice ? "#e1e1e1" : "inherit",
        },
      },
    },
  },
});

const ms_etrade_theme = createTheme({
  palette: {
    primary: {
      main: "#5628d9",
    },
    secondary: { main: "#4ea3eb" },
    background: {
      //@ts-expect-error
      dark: "#dbdbdb",
      default: "#f6f6f6",
      paper: "#ffffff",
    },
    error: { main: "#ee0221" },
    success: { main: "#56a71e" },
    warning: { main: "#f0d918" },
    info: { main: "#4066e3" },
  },
  shape: {
    borderRadius: 8,
  },
  spacing: 8,
typography: {
    htmlFontSize: isMobileDevice ? 14 : 16,
    fontSize: isMobileDevice ? 12 : 14,
    h1: {
      fontSize: isMobileDevice ? "4.8rem" : "6rem",
    },
    h2: {
      fontSize: isMobileDevice ? "3rem" : "3.75rem",
    },
    h3: {
      fontSize: isMobileDevice ? "2.4rem" : "3rem",
    },
    h4: {
      fontSize: isMobileDevice ? "1.7rem" : "2.125rem",
    },
    h5: {
      fontSize: isMobileDevice ? "1.5rem" : "1.875rem",
    },
    h6: { fontWeight: 400, fontSize: isMobileDevice ? "1.2rem" : "1.5rem" },

    body1: {
      fontSize: isMobileDevice ? "0.84rem" : "1.05rem",
    },
    body2: {
      fontSize: isMobileDevice ? "0.76rem" : "0.95rem",
    },
    subtitle1: {
      fontSize: isMobileDevice ? "0.8rem" : "1rem",
    },
    subtitle2: {
      fontWeight: 400,
      fontSize: isMobileDevice ? "0.7rem" : "0.875rem",
    },
    button: {
      fontSize: isMobileDevice ? "0.7rem" : "0.875rem",
    },
    caption: {
      fontSize: isMobileDevice ? "0.68rem" : "0.85rem",
    },
  },
  components: {
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          fontSize: "1rem",
        },
      },
    },
    MuiDialogTitle: {
      styleOverrides: {
        root: {
          color: "#4066e3",
          backgroundColor: isMobileDevice ? "inherit" : "inherit",
        },
      },
    },
    MuiFormHelperText: {
      styleOverrides: {
        root: {
          fontSize: "0.85rem",
        },
      },
    },
    MuiDialog: {
      defaultProps: {
        TransitionComponent: isMobileDevice ? Slide : undefined,
        fullScreen: isMobileDevice,
        TransitionProps: isMobileDevice ? ({ direction: "up" } as SlideProps) : undefined,
      },
    },
    MuiDialogActions: {
      styleOverrides: {
        root: {
          backgroundColor: isMobileDevice ? "#e1e1e1" : "inherit",
        },
      },
    },
  },
});

const eq_theme = createTheme({
  palette: {
    primary: { main: "#e30002" },
    secondary: { main: "#357a9a" },
    background: {
      //@ts-expect-error
      dark: "#b9b9b9",
      default: "#eff3f5",
      paper: "#ffffff",
    },
    error: { main: "#378a89" },
    success: { main: "#41b46c" },
    warning: { main: "#ff0000" },
    info: { main: "#3c3c3c" },
  },
  shape: {
    borderRadius: 8,
  },
  spacing: 8,
typography: {
    htmlFontSize: isMobileDevice ? 14 : 16,
    fontSize: isMobileDevice ? 12 : 14,
    h1: {
      fontSize: isMobileDevice ? "4.8rem" : "6rem",
    },
    h2: {
      fontSize: isMobileDevice ? "3rem" : "3.75rem",
    },
    h3: {
      fontSize: isMobileDevice ? "2.4rem" : "3rem",
    },
    h4: {
      fontSize: isMobileDevice ? "1.7rem" : "2.125rem",
    },
    h5: {
      fontSize: isMobileDevice ? "1.5rem" : "1.875rem",
    },
    h6: { fontWeight: 400, fontSize: isMobileDevice ? "1.2rem" : "1.5rem" },

    body1: {
      fontSize: isMobileDevice ? "0.84rem" : "1.05rem",
    },
    body2: {
      fontSize: isMobileDevice ? "0.76rem" : "0.95rem",
    },
    subtitle1: {
      fontSize: isMobileDevice ? "0.8rem" : "1rem",
    },
    subtitle2: {
      fontWeight: 400,
      fontSize: isMobileDevice ? "0.7rem" : "0.875rem",
    },
    button: {
      fontSize: isMobileDevice ? "0.7rem" : "0.875rem",
    },
    caption: {
      fontSize: isMobileDevice ? "0.68rem" : "0.85rem",
    },
  },
  components: {
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          fontSize: "1rem",
        },
      },
    },
    MuiDialogTitle: {
      styleOverrides: {
        root: {
          color: "#3c3c3c",
          backgroundColor: isMobileDevice ? "inherit" : "inherit",
        },
      },
    },
    MuiFormHelperText: {
      styleOverrides: {
        root: {
          fontSize: "0.85rem",
        },
      },
    },
    MuiDialog: {
      defaultProps: {
        TransitionComponent: isMobileDevice ? Slide : undefined,
        fullScreen: isMobileDevice,
        TransitionProps: isMobileDevice ? ({ direction: "up" } as SlideProps) : undefined,
      },
    },
    MuiDialogActions: {
      styleOverrides: {
        root: {
          backgroundColor: isMobileDevice ? "#e1e1e1" : "inherit",
        },
      },
    },
  },
});

const empower_theme = createTheme({
  palette: {
    primary: { main: "#3461b3" },
    secondary: { main: "#4a9cde" },
    background: {
      //@ts-expect-error
      dark: "#e1e5e8",
      default: "#ededed",
      paper: "#ffffff",
    },
    error: { main: "#d7212a" },
    success: { main: "#2c8a00" },
    warning: { main: "#eb7028" },
    info: { main: "#132158" },
  },
  shape: {
    borderRadius: 8,
  },
  spacing: 8,
typography: {
    htmlFontSize: isMobileDevice ? 14 : 16,
    fontSize: isMobileDevice ? 12 : 14,
    h1: {
      fontSize: isMobileDevice ? "4.8rem" : "6rem",
    },
    h2: {
      fontSize: isMobileDevice ? "3rem" : "3.75rem",
    },
    h3: {
      fontSize: isMobileDevice ? "2.4rem" : "3rem",
    },
    h4: {
      fontSize: isMobileDevice ? "1.7rem" : "2.125rem",
    },
    h5: {
      fontSize: isMobileDevice ? "1.5rem" : "1.875rem",
    },
    h6: { fontWeight: 400, fontSize: isMobileDevice ? "1.2rem" : "1.5rem" },

    body1: {
      fontSize: isMobileDevice ? "0.84rem" : "1.05rem",
    },
    body2: {
      fontSize: isMobileDevice ? "0.76rem" : "0.95rem",
    },
    subtitle1: {
      fontSize: isMobileDevice ? "0.8rem" : "1rem",
    },
    subtitle2: {
      fontWeight: 400,
      fontSize: isMobileDevice ? "0.7rem" : "0.875rem",
    },
    button: {
      fontSize: isMobileDevice ? "0.7rem" : "0.875rem",
    },
    caption: {
      fontSize: isMobileDevice ? "0.68rem" : "0.85rem",
    },
  },
  components: {
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          fontSize: "1rem",
        },
      },
    },
    MuiDialogTitle: {
      styleOverrides: {
        root: {
          color: "#132158",
          backgroundColor: isMobileDevice ? "inherit" : "inherit",
        },
      },
    },
    MuiFormHelperText: {
      styleOverrides: {
        root: {
          fontSize: "0.85rem",
        },
      },
    },
    MuiDialog: {
      defaultProps: {
        TransitionComponent: isMobileDevice ? Slide : undefined,
        fullScreen: isMobileDevice,
        TransitionProps: isMobileDevice ? ({ direction: "up" } as SlideProps) : undefined,
      },
    },
    MuiDialogActions: {
      styleOverrides: {
        root: {
          backgroundColor: isMobileDevice ? "#e1e1e1" : "inherit",
        },
      },
    },
  },
});

const ubs_theme = createTheme({
  palette: {
    primary: { main: "#99adc2" },
    secondary: { main: "#a16651" },
    background: {
      //@ts-expect-error
      dark: "#f2f4f4",
      default: "#fafbfb",
      paper: "#ffffff",
    },
    error: { main: "#585248" },
    success: { main: "#a8ca3c" },
    warning: { main: "#ed9125" },
    info: { main: "#162f4d" },
  },
  shape: {
    borderRadius: 8,
  },
  spacing: 8,
typography: {
    htmlFontSize: isMobileDevice ? 14 : 16,
    fontSize: isMobileDevice ? 12 : 14,
    h1: {
      fontSize: isMobileDevice ? "4.8rem" : "6rem",
    },
    h2: {
      fontSize: isMobileDevice ? "3rem" : "3.75rem",
    },
    h3: {
      fontSize: isMobileDevice ? "2.4rem" : "3rem",
    },
    h4: {
      fontSize: isMobileDevice ? "1.7rem" : "2.125rem",
    },
    h5: {
      fontSize: isMobileDevice ? "1.5rem" : "1.875rem",
    },
    h6: { fontWeight: 400, fontSize: isMobileDevice ? "1.2rem" : "1.5rem" },

    body1: {
      fontSize: isMobileDevice ? "0.84rem" : "1.05rem",
    },
    body2: {
      fontSize: isMobileDevice ? "0.76rem" : "0.95rem",
    },
    subtitle1: {
      fontSize: isMobileDevice ? "0.8rem" : "1rem",
    },
    subtitle2: {
      fontWeight: 400,
      fontSize: isMobileDevice ? "0.7rem" : "0.875rem",
    },
    button: {
      fontSize: isMobileDevice ? "0.7rem" : "0.875rem",
    },
    caption: {
      fontSize: isMobileDevice ? "0.68rem" : "0.85rem",
    },
  },
  components: {
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          fontSize: "1rem",
        },
      },
    },
    MuiDialogTitle: {
      styleOverrides: {
        root: {
          color: "#162f4d",
          backgroundColor: isMobileDevice ? "inherit" : "inherit",
        },
      },
    },
    MuiFormHelperText: {
      styleOverrides: {
        root: {
          fontSize: "0.85rem",
        },
      },
    },
    MuiDialog: {
      defaultProps: {
        TransitionComponent: isMobileDevice ? Slide : undefined,
        fullScreen: isMobileDevice,
        TransitionProps: isMobileDevice ? ({ direction: "up" } as SlideProps) : undefined,
      },
    },
    MuiDialogActions: {
      styleOverrides: {
        root: {
          backgroundColor: isMobileDevice ? "#e1e1e1" : "inherit",
        },
      },
    },
  },
});

const schwab_theme = createTheme({
  palette: {
    primary: { main: "#387cac" },
    secondary: { main: "#4ca0df" },
    background: {
      //@ts-expect-error
      dark: "#cccccc",
      default: "#f4f5f6",
      paper: "#ffffff",
    },
    error: { main: "#e71521" },
    success: { main: "#257900" },
    warning: { main: "#e29802" },
    info: { main: "#326f99" },
  },
  shape: {
    borderRadius: 8,
  },
  spacing: 8,
typography: {
    htmlFontSize: isMobileDevice ? 14 : 16,
    fontSize: isMobileDevice ? 12 : 14,
    h1: {
      fontSize: isMobileDevice ? "4.8rem" : "6rem",
    },
    h2: {
      fontSize: isMobileDevice ? "3rem" : "3.75rem",
    },
    h3: {
      fontSize: isMobileDevice ? "2.4rem" : "3rem",
    },
    h4: {
      fontSize: isMobileDevice ? "1.7rem" : "2.125rem",
    },
    h5: {
      fontSize: isMobileDevice ? "1.5rem" : "1.875rem",
    },
    h6: { fontWeight: 400, fontSize: isMobileDevice ? "1.2rem" : "1.5rem" },

    body1: {
      fontSize: isMobileDevice ? "0.84rem" : "1.05rem",
    },
    body2: {
      fontSize: isMobileDevice ? "0.76rem" : "0.95rem",
    },
    subtitle1: {
      fontSize: isMobileDevice ? "0.8rem" : "1rem",
    },
    subtitle2: {
      fontWeight: 400,
      fontSize: isMobileDevice ? "0.7rem" : "0.875rem",
    },
    button: {
      fontSize: isMobileDevice ? "0.7rem" : "0.875rem",
    },
    caption: {
      fontSize: isMobileDevice ? "0.68rem" : "0.85rem",
    },
  },
  components: {
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          fontSize: "1rem",
        },
      },
    },
    MuiDialogTitle: {
      styleOverrides: {
        root: {
          color: "#326f99",
          backgroundColor: isMobileDevice ? "inherit" : "inherit",
        },
      },
    },
    MuiFormHelperText: {
      styleOverrides: {
        root: {
          fontSize: "0.85rem",
        },
      },
    },
    MuiDialog: {
      defaultProps: {
        TransitionComponent: isMobileDevice ? Slide : undefined,
        fullScreen: isMobileDevice,
        TransitionProps: isMobileDevice ? ({ direction: "up" } as SlideProps) : undefined,
      },
    },
    MuiDialogActions: {
      styleOverrides: {
        root: {
          backgroundColor: isMobileDevice ? "#e1e1e1" : "inherit",
        },
      },
    },
  },
});

const fidelity_theme = createTheme({
  palette: {
    primary: { main: "#368728" },
    secondary: { main: "#c18e3b" },
    background: {
      //@ts-expect-error
      dark: "#f2f2f2",
      default: "#ffffff",
      paper: "#ffffff",
    },
    error: { main: "#dc1616" },
    success: { main: "#368728" },
    warning: { main: "#fdfae6" },
    info: { main: "#929292" },
  },
  shape: {
    borderRadius: 8,
  },
  spacing: 8,
typography: {
    htmlFontSize: isMobileDevice ? 14 : 16,
    fontSize: isMobileDevice ? 12 : 14,
    h1: {
      fontSize: isMobileDevice ? "4.8rem" : "6rem",
    },
    h2: {
      fontSize: isMobileDevice ? "3rem" : "3.75rem",
    },
    h3: {
      fontSize: isMobileDevice ? "2.4rem" : "3rem",
    },
    h4: {
      fontSize: isMobileDevice ? "1.7rem" : "2.125rem",
    },
    h5: {
      fontSize: isMobileDevice ? "1.5rem" : "1.875rem",
    },
    h6: { fontWeight: 400, fontSize: isMobileDevice ? "1.2rem" : "1.5rem" },

    body1: {
      fontSize: isMobileDevice ? "0.84rem" : "1.05rem",
    },
    body2: {
      fontSize: isMobileDevice ? "0.76rem" : "0.95rem",
    },
    subtitle1: {
      fontSize: isMobileDevice ? "0.8rem" : "1rem",
    },
    subtitle2: {
      fontWeight: 400,
      fontSize: isMobileDevice ? "0.7rem" : "0.875rem",
    },
    button: {
      fontSize: isMobileDevice ? "0.7rem" : "0.875rem",
    },
    caption: {
      fontSize: isMobileDevice ? "0.68rem" : "0.85rem",
    },
  },
  components: {
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          fontSize: "1rem",
        },
      },
    },
    MuiDialogTitle: {
      styleOverrides: {
        root: {
          color: "#929292",
          backgroundColor: isMobileDevice ? "inherit" : "inherit",
        },
      },
    },
    MuiFormHelperText: {
      styleOverrides: {
        root: {
          fontSize: "0.85rem",
        },
      },
    },
  },
});

const baml_theme = createTheme({
  palette: {
    primary: { main: "#012269" },
    secondary: { main: "#b8eeff" },
    background: {
      //@ts-expect-error
      dark: "#fafafa",
      default: "#fafafa",
      paper: "#ffffff",
    },
    error: { main: "#ea1437" },
    success: { main: "#2a7c32" },
    warning: { main: "#a61d47" },
    info: { main: "#0391d8" },
  },
  shape: {
    borderRadius: 8,
  },
  spacing: 8,
typography: {
    htmlFontSize: isMobileDevice ? 14 : 16,
    fontSize: isMobileDevice ? 12 : 14,
    h1: {
      fontSize: isMobileDevice ? "4.8rem" : "6rem",
    },
    h2: {
      fontSize: isMobileDevice ? "3rem" : "3.75rem",
    },
    h3: {
      fontSize: isMobileDevice ? "2.4rem" : "3rem",
    },
    h4: {
      fontSize: isMobileDevice ? "1.7rem" : "2.125rem",
    },
    h5: {
      fontSize: isMobileDevice ? "1.5rem" : "1.875rem",
    },
    h6: { fontWeight: 400, fontSize: isMobileDevice ? "1.2rem" : "1.5rem" },

    body1: {
      fontSize: isMobileDevice ? "0.84rem" : "1.05rem",
    },
    body2: {
      fontSize: isMobileDevice ? "0.76rem" : "0.95rem",
    },
    subtitle1: {
      fontSize: isMobileDevice ? "0.8rem" : "1rem",
    },
    subtitle2: {
      fontWeight: 400,
      fontSize: isMobileDevice ? "0.7rem" : "0.875rem",
    },
    button: {
      fontSize: isMobileDevice ? "0.7rem" : "0.875rem",
    },
    caption: {
      fontSize: isMobileDevice ? "0.68rem" : "0.85rem",
    },
  },
  components: {
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          fontSize: "1rem",
        },
      },
    },
    MuiDialogTitle: {
      styleOverrides: {
        root: {
          color: "#0391d8",
          backgroundColor: isMobileDevice ? "inherit" : "inherit",
        },
      },
    },
    MuiFormHelperText: {
      styleOverrides: {
        root: {
          fontSize: "0.85rem",
        },
      },
    },
  },
});

const gs_theme = createTheme({
  palette: {
    primary: { main: "#3d78c5" },
    secondary: { main: "#7bbdf1" },
    background: {
      //@ts-expect-error
      dark: "#f3f5f9",
      default: "#f1f2f7",
      paper: "#ffffff",
    },
    error: { main: "#c73933" },
    success: { main: "#398025" },
    warning: { main: "#f07e16" },
    info: { main: "#21253e" },
  },
  shape: {
    borderRadius: 8,
  },
  spacing: 8,
  typography: {
    h6: { fontWeight: 400 },
    body1: {
      fontSize: "1.05rem",
    },
    body2: {
      fontSize: "0.95rem",
    },
    subtitle2: {
      fontWeight: 400,
    },
    caption: {
      fontSize: "0.85rem",
    },
  },
  components: {
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          fontSize: "1rem",
        },
      },
    },
    MuiDialogTitle: {
      styleOverrides: {
        root: {
          color: "#21253e",
        },
      },
    },
    MuiFormHelperText: {
      styleOverrides: {
        root: {
          fontSize: "0.85rem",
        },
      },
    },
  },
});

export function getBrandedTheme(branding: BrandingType = DefaultPersonalizationConfig.branding) {
  switch (branding) {
    case "jpm":
      return jpm_theme;
    case "ms_etrade":
      return ms_etrade_theme;
    case "eq":
      return eq_theme;
    case "empower":
      return empower_theme;
    case "ubs":
      return ubs_theme;
    case "schwab":
      return schwab_theme;
    case "fidelity":
      return fidelity_theme;
    case "baml":
      return baml_theme;
    case "gs":
        return gs_theme;
    case "coinbase":
      return salus_theme;
    case "salus":
      return salus_theme;
    default:
      return salus_theme;
  }
}

const theme = getBrandedTheme();

export default theme;
